import { isMembership, isGlobalUnlimited, isGlobalLimited, isUnlimited, isLimited, isSimPlan, isWelcomeOffer, isBonusMinutes, isDeal } from '@/utils/bucketTypes'
import { unique, isEmpty } from '@/utils/array'
import { objectHasKeys } from '@/utils/comparators'

/**
* Returns true if user has Rebtel Plus membership subscription.
* @param {object} state
*/
const hasMembership = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isMembership(b))

  return !isEmpty(filtered)
}

/**
* Returns true if user has Global Unlimited product (trial or paid).
* @param {object} state
*/
const hasGlobalUnlimited = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isGlobalUnlimited(b))

  return !isEmpty(filtered)
}

/**
* Returns true if user has Global Limited product (trial or paid).
* @param {object} state
*/
const hasGlobalLimited = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isGlobalLimited(b))

  return !isEmpty(filtered)
}

/**
* Returns true if user has Global Limited or Unlimited product (trial or paid).
* @param {object} state
*/
const hasGlobalProduct = (state, getters) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isGlobalUnlimited(b) || isGlobalLimited(b))

  return !isEmpty(filtered)
}

/**
* Returns true if user has Country Unlimited product (trial or paid).
* @param {object} state
*/
const hasUnlimited = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b))

  return !isEmpty(filtered)
}

/**
* Returns true if user has an Unlimited product (trial or paid) to the specified country.
* Global Unlimited is included in the calculation.
* @param {object} state
*/
const hasUnlimitedToCountry = (state) => (countryCode) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b))
  let has = false

  filtered.forEach(function (b) {
    if (b.product.targetedCountries.filter((t) => t === countryCode).length > 0) {
      has = true
    }
  })

  return has
}

/**
* Returns true if user has Country Limited product (trial or paid).
* @param {object} state
*/
const hasLimited = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isLimited(b))

  return !isEmpty(filtered)
}

/**
* Returns true if user any active SIM bucket.
* @param {object} state
*/
const hasSimPlan = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isSimPlan(b))

  return !isEmpty(filtered)
}

/**
* Creates an array with iso-alpha-2 country codes based on user products.
* Products with only 1 targeted country is included.
* Any multi country product will be excluded from this list (Global Unlimited, Global Limited)
* @param {object} state
*/
const destinationCountries = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b) || isLimited(b) || isWelcomeOffer(b) || isDeal(b))

  let result = []

  filtered.forEach(function (bucket) {
    if (bucket.product.targetedCountries.length === 1) {
      result.push(bucket.product.targetedCountry)
    }
  })

  return unique(result)
}

/**
* Counts the number of communication products belonging to user account.
* Includes Global, Unlimited, Limited and Welcome offers.
* Both trial and paid products are counted.
* @param {object} state
*/
const productCount = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b) || isLimited(b) || isWelcomeOffer(b))

  return filtered.length
}

/**
* Counts the number of communication products belonging to user account.
* Includes Global, Unlimited, Limited, Welcome offers and Deals.
* Both trial and paid products are counted.
* @param {object} state
*/
const productCountForAll = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b) || isLimited(b) || isWelcomeOffer(b) || isDeal(b))

  return filtered.length
}

/**
* Counts the number of communication products belonging to user account.
* Includes Deals.
* @param {object} state
*/
const productCountDeals = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isDeal(b))

  return filtered.length
}

/**
 *
 */
const remainingDataPercentage = (state) => {
  let buckets = state.dataBuckets
  let remaining = 0
  let numberOfBuckets = 0

  if (buckets) {
    for (const sim of buckets) {
      for (const bucket of sim.databuckets) {
        remaining += bucket.data.remainingPercent
        numberOfBuckets += 1
      }
    }
  }

  return numberOfBuckets > 0 ? (remaining / numberOfBuckets) : 0
}

/**
* Return an array with all bonus minutes buckets
* @param {object} state
*/
const getBonusMinutesBuckets = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isBonusMinutes(b))

  return filtered
}

const getUserProducts = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b) || isLimited(b) || isMembership(b) || isWelcomeOffer(b))

  return filtered
}

const getDealProducts = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isDeal(b))

  return filtered
}

const getUserProductsWithDeals = (state) => {
  let buckets = objectHasKeys(state.buckets)
    ? Object.values(state.buckets)
    : []

  let filtered = buckets.filter((b) => isUnlimited(b) || isLimited(b) || isMembership(b) || isWelcomeOffer(b) || isDeal(b))

  return filtered
}

export default {
  hasMembership,
  hasGlobalUnlimited,
  hasGlobalLimited,
  hasGlobalProduct,
  hasUnlimited,
  hasUnlimitedToCountry,
  hasLimited,
  hasSimPlan,
  destinationCountries,
  productCount,
  productCountForAll,
  productCountDeals,
  remainingDataPercentage,
  getBonusMinutesBuckets,
  getUserProducts,
  getDealProducts,
  getUserProductsWithDeals
}



// WEBPACK FOOTER //
// ./src/store/modules/user/products/getters.js